import React, { useContext, useEffect } from 'react';

import { TrackEventContext } from 'common/containers/EventContainer';
import HiveLandingHelmet from 'common/helmets/HiveLandingHelmet';
import LandingFooterCTA from 'common/LandingFooterCTA';
import LazyLoadedImage from 'common/LazyLoadedImage';
import UppercaseHeader from 'common/UppercaseHeader';
import LandingCaseStudyHeadingText from 'common/www/LandingCaseStudyHeadingText';
import HiveWordmark from 'img/landing/hive-wordmark.webp';
import KelleyMichaela from 'img/landing/kelley-and-michaela.webp';

import 'css/components/www/_LandingCustomersPage.scss';

const HiveLanding = () => {
  const trackEvent = useContext(TrackEventContext);

  useEffect(() => {
    trackEvent('Viewed Hive Case Study');
  }, [trackEvent]);

  return (
    <div className="landingHive landingCustomersPage">
      <HiveLandingHelmet />
      <div className="fullWidthContainer">
        <div className="header">
          <LandingCaseStudyHeadingText title="How Hive uses Canny to collaborate across departments and increase their NPS" />
          <div className="headerImage">
            <div className="nametag">
              <div className="name">Michaela Rollings</div>
              <div className="title">Head of Brand & Content at&nbsp;Hive</div>
            </div>
            <div className="nametag">
              <div className="name">Kelley Bunge</div>
              <div className="title">Director of Product at&nbsp;Hive</div>
            </div>
            <LazyLoadedImage
              loading="eager"
              src={KelleyMichaela}
              alt="Michaela Rollings and Kelley Bunge"
            />
          </div>
        </div>
      </div>
      <div className="mainContent">
        <div className="mainContentText">
          <div className="left">
            <p>
              Hive helps teams collaborate and get more done together. Two main things set
              Hive&nbsp;apart:
            </p>
            <ol className="numberedOrderedList">
              <li>
                During the COVID-19 pandemic, Hive evolved as remote work became more&nbsp;prominent
              </li>
              <li>
                Around the same time, Hive adjusted their product development process. Now, they put
                special emphasis on user feedback. They're proudly "built by&nbsp;users".
              </li>
            </ol>
            <p>
              Naturally, Hive's product team needed a tool to help them listen to their users. And,
              the marketing team at Hive was looking for a way to learn about product updates and
              market them&nbsp;better.
            </p>
            <p>
              We spoke to Kelley Bunge, Director of Product, and Michaela Rollings, Head of Brand &
              Content, to learn about their experience with&nbsp;Canny.
            </p>
            <h2 className="sectionTitle">Challenge</h2>
            <p>
              Let’s travel back in time. The year is 2019. Team Hive is all in the office. People
              come up to each other’s desks and verbally pass on feature requests and ideas. Some
              feedback would get sent around through emails. And other requests appeared in the chat
              inside Hive’s&nbsp;tool.
            </p>
            <p>
              Naturally, there was no single source of truth. On top of that, only the “loudest”
              customers who spoke up about their issues would get them resolved. Smaller teams
              didn’t always get a chance to share their&nbsp;feedback.
            </p>
            <p className="quote">
              &ldquo;We would rely on anecdotal feedback. Getting a cohesive look at what customers
              needed and wanted was hard. And that is very important for&nbsp;us.&rdquo;
            </p>
            <h2 className="sectionTitle">Solution</h2>
            <p>
              Team Hive came across Canny and wanted to test it out. Instead of sharing their Canny
              portal with the world right away, they decided to use it internally&nbsp;first.
            </p>
            <p>
              Once they saw the value of Canny, they invited their users to their new
              community&nbsp;platform.
            </p>
            <p className="quote">
              &ldquo;In 2020, we shifted to being customer-first and community-built. Canny fit that
              mission perfectly and helped us with that&nbsp;transition.&rdquo;
            </p>
            <p>Today, many teams at Hive use Canny&nbsp;daily.</p>
            <p>
              Kelley and her product team go through new requests, analyze trends, and engage with
              the&nbsp;community.
            </p>
            <div className="bodyVideo">
              <iframe
                width="600"
                height="337"
                src="https://www.youtube.com/embed/9m5IurVVgns?si=nr-RaoaV7QGS3WoZ"
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen></iframe>
            </div>
            <p>
              Michaela and the marketing team get ideas for their content. They find out what the
              product team is working on and what users care about. Here are a few ways they
              leverage Canny that they&nbsp;shared:
            </p>
            <ul>
              <li>Get a sense of the overall sentiment around potential new&nbsp;features.</li>
              <li>
                Ask follow-up questions to clarify and learn more about feature&nbsp;requests.
              </li>
              <li>
                See which features are getting lots of attention. Include them in newsletters and
                other promotional&nbsp;materials.
              </li>
              <li>
                Give shoutouts to active community members in your promotional materials. They’ll
                feel&nbsp;special.
              </li>
              <li>
                Reach out to very active users and invite them to do a case study with&nbsp;you.
              </li>
            </ul>
            <p className="quote">
              &ldquo;Canny is saving us about 2-3 hours per week on product marketing. Instead of
              going back and forth between various tools, everything lives in Canny now. It's our
              one source of&nbsp;truth.&rdquo;
            </p>
            <div className="bodyVideo">
              <iframe
                width="600"
                height="337"
                src="https://www.youtube.com/embed/bgPXrDxH3Tk?si=px3Lp4uyF_1qQpYF"
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen></iframe>
            </div>
            <p>
              The sales team at Hive uses Canny to improve their process too. Any completed feature
              request, no matter how small, can lead to a sale. Existing customers who submit
              feature requests help account managers and customer success teams&nbsp;too.
            </p>
            <p>
              To streamline all of these processes, Hive uses Canny’s Zapier integration. This helps
              them coordinate all project statuses across their&nbsp;tools.
            </p>
            <p className="quote">
              &ldquo;This integration takes the burden off our team to keep everything up to date.
              Also, when a post is marked “complete,” our customers get
              automatically&nbsp;notified.&rdquo;
            </p>
            <p>Every team points to Canny’s ease of&nbsp;use.</p>
            <p className="quote">
              &ldquo;When I train new team members on Canny, it’s very simple and straightforward.
              From release notes to voting, it’s very intuitive. Many other tools aren’t like that,
              so Canny really stands&nbsp;out.&rdquo;
            </p>
            <p>
              Hive also had a separate area of their site for release notes. With Canny’s Changelog,
              managing those and keeping everyone updated is much&nbsp;easier.
            </p>
            <h2 className="sectionTitle">Results</h2>
            <p>
              Hive attributes many improvements in productivity, communication, and customer
              satisfaction to&nbsp;Canny.
            </p>
            <p>
              Keeping up with customer communications is now a lot easier. Instead of asking each
              other for a status update, teams at Hive can just refer to Canny. Each team feels like
              their input&nbsp;matters.
            </p>
            <p className="quote">
              &ldquo;The sales team passes on feedback and feels like the product [team] really
              listens. And product [team] feels like marketing is on the same page. So even our
              internal feedback loop is improved thanks to&nbsp;Canny.&rdquo;
            </p>
            <p>
              The product team at Hive also points out that Canny helped them achieve
              more&nbsp;reach.
            </p>
            <p className="quote">
              &ldquo;We still do customer interviews and other day-to-day client communications.
              Doing thousands of interviews would take a really long time though. The reach that
              Canny provides is beyond what we could&nbsp;achieve.&rdquo;
            </p>
            <p>
              Above all, Hive’s product team now knows exactly what to focus on. Thanks to Canny’s
              prioritization, they can now inform their roadmap and ultimately delight their
              customers. It’s clear that Hive’s users enjoy Canny as&nbsp;well.
            </p>
            <p className="quote">
              &ldquo;Our users started to comment and help each other out in Canny. For example,
              someone will post about an issue, and someone else will comment and suggest a
              workaround or a resolution. Seeing a community fostering within Canny
              is&nbsp;beautiful.&rdquo;
            </p>
            <p>To measure Canny’s value, Hive looks at a few&nbsp;metrics.</p>
            <ul>
              <li>Number of votes</li>
              <li>Number of completed feature&nbsp;requests</li>
              <li>NPS score</li>
              <li>Time saved on product&nbsp;marketing</li>
            </ul>
            <p className="quote">
              &ldquo;If you look at our NPS chart, it’s pretty clear when we’ve switched to being
              community-focused. We’ve seen our NPS score go up consistently. And Canny certainly
              played a&nbsp;role.&rdquo;
            </p>
            <p>
              Kelley and Michaela have been using Canny to its full potential. If you’d like to make
              the most out of it too, follow their&nbsp;advice.
            </p>
            <p className="quote">
              &ldquo;Customize your Canny portal. We use custom fields to ensure we get the exact
              output we need from our&nbsp;users.&rdquo;
            </p>
            <p>
              Ready to start saving time, delighting your users, and building awesome products? Get
              started with Canny&nbsp;today!
            </p>
          </div>
          <div className="right">
            <LazyLoadedImage alt="Hive Wordmark" className="hiveWordmark" src={HiveWordmark} />
            Hive is a project management and productivity tool. Thousands of companies improve
            collaboration, stay connected, and get work done faster with&nbsp;Hive.
            <a href="https://www.hive.com/" rel="noopener" target="_blank">
              hive.com
            </a>
            <div className="stat">
              <UppercaseHeader>Employees</UppercaseHeader>
              <p>65</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Founded</UppercaseHeader>
              <p>2016</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Using Canny since</UppercaseHeader>
              <p>2019</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Votes on Canny</UppercaseHeader>
              <p>80,000+</p>
            </div>
          </div>
        </div>
        <LandingFooterCTA style="solid" expanded={true} />
      </div>
    </div>
  );
};
export default HiveLanding;
